/**
 * EvidentaOreTab - Componenta pentru tab-ul Evidență Ore
 * Extrasă din AdminDashboard.jsx pentru modularizare și performanță
 * Afișează o oglindă a pontajului cu programul de lucru detaliat
 */
import { memo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Download, FileText, Clock, Search, X } from 'lucide-react';

const EvidentaOreTab = memo(function EvidentaOreTab({
  pontajMonth,
  pontajYear,
  pontajData,
  pontajLoading,
  drivers,
  setPontajMonth,
  setPontajYear,
  exportEvidentaOrePDF,
  exportEvidentaOreExcel,
  isSarbatoare,
  getSarbatoareNume,
  getDisplayName,
  getPontajValue,
  loading
}) {
  // State pentru căutare angajat
  const [searchQuery, setSearchQuery] = useState('');
  
  const daysInMonth = new Date(pontajYear, pontajMonth, 0).getDate();
  
  // Navigation functions
  const goToPrevMonth = () => {
    if (pontajMonth === 1) {
      setPontajMonth(12);
      setPontajYear(pontajYear - 1);
    } else {
      setPontajMonth(pontajMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (pontajMonth === 12) {
      setPontajMonth(1);
      setPontajYear(pontajYear + 1);
    } else {
      setPontajMonth(pontajMonth + 1);
    }
  };

  const goToCurrentMonth = () => {
    setPontajMonth(new Date().getMonth() + 1);
    setPontajYear(new Date().getFullYear());
  };

  // Filter and sort employees same as Pontaj
  const evidentaEmployees = drivers.filter(d => {
    if (d.role === 'admin') return false;
    if (d.in_pontaj === false) return false;
    if (d.pontaj_pana_la) {
      const endDate = new Date(d.pontaj_pana_la);
      const endYear = endDate.getFullYear();
      const endMonth = endDate.getMonth() + 1;
      if (pontajYear > endYear || (pontajYear === endYear && pontajMonth > endMonth)) {
        return false;
      }
    }
    if (d.angajat_de_la) {
      const startDate = new Date(d.angajat_de_la);
      const startYear = startDate.getFullYear();
      const startMonth = startDate.getMonth() + 1;
      if (pontajYear < startYear || (pontajYear === startYear && pontajMonth < startMonth)) {
        return false;
      }
    }
    
    // Filtrare după căutare
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const fullName = `${d.nume || ''} ${d.prenume || ''}`.toLowerCase();
      const username = (d.username || '').toLowerCase();
      if (!fullName.includes(query) && !username.includes(query)) {
        return false;
      }
    }
    
    return true;
  }).sort((a, b) => {
    const aHasContract = a.numar_contract && a.numar_contract.trim() !== '';
    const bHasContract = b.numar_contract && b.numar_contract.trim() !== '';
    if (aHasContract && !bHasContract) return -1;
    if (!aHasContract && bHasContract) return 1;
    if (!aHasContract && !bHasContract) return 0;
    const aNum = parseInt(a.numar_contract) || 999999;
    const bNum = parseInt(b.numar_contract) || 999999;
    return aNum - bNum;
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Evidență Ore
              </CardTitle>
              <CardDescription>Oglindă automată a pontajului - modificările se fac din tab-ul Pontaj</CardDescription>
            </div>
            
            {/* Month/Year Navigation */}
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={goToPrevMonth}>
                ← Luna anterioară
              </Button>
              
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
                <Select value={String(pontajMonth)} onValueChange={(v) => setPontajMonth(parseInt(v))}>
                  <SelectTrigger className="w-[130px] border-0 bg-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Ianuarie</SelectItem>
                    <SelectItem value="2">Februarie</SelectItem>
                    <SelectItem value="3">Martie</SelectItem>
                    <SelectItem value="4">Aprilie</SelectItem>
                    <SelectItem value="5">Mai</SelectItem>
                    <SelectItem value="6">Iunie</SelectItem>
                    <SelectItem value="7">Iulie</SelectItem>
                    <SelectItem value="8">August</SelectItem>
                    <SelectItem value="9">Septembrie</SelectItem>
                    <SelectItem value="10">Octombrie</SelectItem>
                    <SelectItem value="11">Noiembrie</SelectItem>
                    <SelectItem value="12">Decembrie</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={String(pontajYear)} onValueChange={(v) => setPontajYear(parseInt(v))}>
                  <SelectTrigger className="w-[90px] border-0 bg-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button variant="outline" size="sm" onClick={goToNextMonth}>
                Luna următoare →
              </Button>
              
              <Button variant="default" size="sm" onClick={goToCurrentMonth}>
                Luna curentă
              </Button>
              
              {/* Export buttons */}
              <Button 
                variant="outline" 
                size="sm"
                onClick={exportEvidentaOrePDF}
                disabled={loading}
                className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
              >
                <Download className="w-4 h-4 mr-1" />
                {loading ? '...' : 'PDF'}
              </Button>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={exportEvidentaOreExcel}
                disabled={loading}
                className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
              >
                <Download className="w-4 h-4 mr-1" />
                {loading ? '...' : 'Excel'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search Bar */}
          <div className="mb-4 flex items-center gap-2">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Caută angajat..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-8"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {searchQuery && (
              <span className="text-sm text-slate-500">
                {evidentaEmployees.length} rezultat{evidentaEmployees.length !== 1 ? 'e' : ''}
              </span>
            )}
          </div>
          
          {/* Info banner */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-blue-800">
            <Clock className="w-4 h-4" />
            <span className="text-sm">Datele sunt sincronizate automat cu Pontajul. Pentru modificări, accesați tab-ul <strong>Pontaj</strong>.</span>
          </div>
          
          {/* Legend */}
          <div className="flex flex-wrap gap-4 mb-4 text-xs">
            <div className="flex items-center gap-1">
              <span className="px-2 py-1 bg-green-50 border border-green-300 rounded text-[8px] leading-tight text-green-700 flex flex-col items-center">
                <span>08:00-12:00</span>
                <span className="text-slate-400">pauza</span>
                <span>13:00-17:00</span>
              </span>
              <span>Program lucru (8h)</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-8 h-5 bg-blue-100 border border-blue-300 rounded text-center text-[10px] leading-5 text-blue-700">CO</span>
              <span>Concediu Odihnă</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-8 h-5 bg-orange-100 border border-orange-300 rounded text-center text-[10px] leading-5 text-orange-700">CM</span>
              <span>Concediu Medical</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-10 h-5 bg-yellow-100 border border-yellow-300 rounded text-center text-[10px] leading-5 text-yellow-700">CFP</span>
              <span>Fără Plată</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-10 h-5 bg-pink-100 border border-pink-300 rounded text-center text-[10px] leading-5 text-pink-700">CNC</span>
              <span>Naștere Copil</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-8 h-5 bg-purple-100 border border-purple-300 rounded text-center text-[10px] leading-5 text-purple-700">CD</span>
              <span>Deces</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-6 h-5 bg-slate-200 border border-slate-300 rounded text-center text-[10px] leading-5 text-slate-600">X</span>
              <span>Liber / Weekend</span>
            </div>
          </div>
          
          {/* Grid Table */}
          {pontajLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <span className="ml-2 text-slate-600">Se încarcă...</span>
            </div>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="sticky left-0 bg-slate-100 z-10 px-2 py-2 text-left font-semibold w-8">Nr.</th>
                    <th className="sticky left-8 bg-slate-100 z-10 px-2 py-2 text-left font-semibold min-w-[180px]">Nume și Prenume</th>
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const dayNum = i + 1;
                      const dayDate = new Date(pontajYear, pontajMonth - 1, dayNum);
                      const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6;
                      const isHoliday = isSarbatoare(dayNum);
                      const holidayName = getSarbatoareNume(dayNum);
                      const dayNames = ['D', 'L', 'Ma', 'Mi', 'J', 'V', 'S'];
                      return (
                        <th 
                          key={dayNum}
                          className={`px-0.5 py-2 text-center font-semibold min-w-[58px] ${
                            isHoliday ? 'bg-violet-200 text-violet-700' : isWeekend ? 'bg-slate-200 text-slate-600' : ''
                          }`}
                          title={isHoliday ? holidayName : ''}
                        >
                          <div className="text-[10px] text-slate-500">{dayNames[dayDate.getDay()]}</div>
                          <div>{dayNum}</div>
                        </th>
                      );
                    })}
                    <th className="px-2 py-2 text-center font-semibold bg-green-100 min-w-[60px]">Total Ore</th>
                  </tr>
                </thead>
                <tbody>
                  {evidentaEmployees.length === 0 ? (
                    <tr>
                      <td colSpan={daysInMonth + 3} className="text-center py-8 text-slate-500">
                        Nu există angajați în evidență pentru această lună
                      </td>
                    </tr>
                  ) : (
                    evidentaEmployees.map((driver, index) => {
                      const employeePontaj = pontajData[driver.id] || {};
                      let totalOre = 0;
                      
                      const hireDate = driver.angajat_de_la ? new Date(driver.angajat_de_la) : null;
                      const hasHireDateThisMonth = hireDate && pontajYear === hireDate.getFullYear() && pontajMonth === hireDate.getMonth() + 1;
                      const hireDateDay = hasHireDateThisMonth ? hireDate.getDate() : null;
                      const monthBeforeHire = hireDate && (pontajYear < hireDate.getFullYear() || (pontajYear === hireDate.getFullYear() && pontajMonth < hireDate.getMonth() + 1));
                      
                      return (
                        <tr key={driver.id} className="border-t hover:bg-slate-50">
                          <td className="sticky left-0 bg-white z-10 px-2 py-1.5 text-center border-r font-medium">
                            {index + 1}
                          </td>
                          <td className="sticky left-8 bg-white z-10 px-2 py-1.5 border-r font-medium truncate max-w-[180px]">
                            {getDisplayName(driver)}
                          </td>
                          {Array.from({ length: daysInMonth }, (_, i) => {
                            const dayNum = i + 1;
                            const dayDate = new Date(pontajYear, pontajMonth - 1, dayNum);
                            const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6;
                            const isHoliday = isSarbatoare(dayNum);
                            const dayData = employeePontaj[dayNum];
                            const value = getPontajValue(dayData);
                            
                            let cellContent = '';
                            let cellClass = 'bg-white';
                            let isWorkHours = false;
                            
                            if (value === 'CO') {
                              cellContent = 'CO';
                              cellClass = 'bg-blue-100 text-blue-700 font-medium';
                            } else if (value === 'CM') {
                              cellContent = 'CM';
                              cellClass = 'bg-orange-100 text-orange-700 font-medium';
                            } else if (value === 'NM') {
                              cellContent = 'NM';
                              cellClass = 'bg-red-100 text-red-700 font-medium';
                            } else if (value === 'CFP') {
                              cellContent = 'CFP';
                              cellClass = 'bg-yellow-100 text-yellow-700 font-medium';
                            } else if (value === 'CNC') {
                              cellContent = 'CNC';
                              cellClass = 'bg-pink-100 text-pink-700 font-medium';
                            } else if (value === 'CD') {
                              cellContent = 'CD';
                              cellClass = 'bg-purple-100 text-purple-700 font-medium';
                            } else if (value === 'DEM' || value === 'X') {
                              cellContent = 'X';
                              cellClass = isHoliday ? 'bg-violet-200 text-violet-700' : 'bg-slate-200 text-slate-500';
                            } else if ((isWeekend || isHoliday) && !value) {
                              cellContent = 'X';
                              cellClass = isHoliday ? 'bg-violet-200 text-violet-700' : 'bg-slate-200 text-slate-500';
                            } else if (typeof value === 'number') {
                              isWorkHours = true;
                              cellClass = 'bg-green-50 text-green-700';
                              totalOre += value;
                            } else if (!isWeekend && !isHoliday && !value) {
                              const today = new Date();
                              const isCurrentMonth = pontajYear === today.getFullYear() && pontajMonth === today.getMonth() + 1;
                              const isFutureMonth = pontajYear > today.getFullYear() || (pontajYear === today.getFullYear() && pontajMonth > today.getMonth() + 1);
                              const isPastOrToday = !isCurrentMonth || dayNum <= today.getDate();
                              const employeeNorma = driver.norma_ore || 8;
                              const dayBeforeHire = monthBeforeHire || (hireDateDay && dayNum < hireDateDay);
                              
                              if (dayBeforeHire) {
                                cellContent = 'X';
                                cellClass = 'bg-slate-200 text-slate-500';
                              } else if (isFutureMonth) {
                                cellContent = '';
                                cellClass = 'bg-slate-50 text-slate-300';
                              } else if (isPastOrToday) {
                                isWorkHours = true;
                                cellClass = 'bg-green-50 text-green-700';
                                totalOre += employeeNorma;
                              } else {
                                cellContent = '';
                                cellClass = 'bg-white text-slate-300';
                              }
                            }
                            
                            const employeeNorma = driver.norma_ore || 8;
                            const actualHours = typeof value === 'number' ? value : employeeNorma;
                            const programStart = driver.program_start || '08:00';
                            const programSfarsit = driver.program_sfarsit || '17:00';
                            const hasPauza = driver.program_pauza !== false;
                            const pauzaStart = driver.pauza_start || '12:00';
                            const pauzaSfarsit = driver.pauza_sfarsit || '13:00';
                            
                            const customProgramStart = dayData?.program_start;
                            const customProgramSfarsit = dayData?.program_sfarsit;
                            const customPauzaStart = dayData?.pauza_start;
                            const customPauzaSfarsit = dayData?.pauza_sfarsit;
                            
                            const renderProgram = () => {
                              if (customProgramStart && customProgramSfarsit) {
                                if (customPauzaStart && customPauzaSfarsit) {
                                  return (
                                    <div className="flex flex-col text-[9px] leading-tight">
                                      <span>{customProgramStart}-{customPauzaStart}</span>
                                      <span className="text-slate-400 text-[7px]">pauza</span>
                                      <span>{customPauzaSfarsit}-{customProgramSfarsit}</span>
                                    </div>
                                  );
                                }
                                return (
                                  <div className="flex flex-col text-[9px] leading-tight">
                                    <span>{customProgramStart}-{customProgramSfarsit}</span>
                                  </div>
                                );
                              }
                              
                              if (actualHours === employeeNorma) {
                                if (hasPauza && employeeNorma === 8) {
                                  return (
                                    <div className="flex flex-col text-[9px] leading-tight">
                                      <span>{programStart}-{pauzaStart}</span>
                                      <span className="text-slate-400 text-[7px]">pauza</span>
                                      <span>{pauzaSfarsit}-{programSfarsit}</span>
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div className="flex flex-col text-[9px] leading-tight">
                                      <span>{programStart}-{programSfarsit}</span>
                                    </div>
                                  );
                                }
                              }
                              
                              const startHour = parseInt(programStart.split(':')[0]);
                              const startMin = parseInt(programStart.split(':')[1]) || 0;
                              const endHour = startHour + actualHours;
                              const calculatedEnd = `${String(endHour).padStart(2, '0')}:${String(startMin).padStart(2, '0')}`;
                              
                              return (
                                <div className="flex flex-col text-[9px] leading-tight">
                                  <span>{programStart}-{calculatedEnd}</span>
                                </div>
                              );
                            };
                            
                            return (
                              <td 
                                key={dayNum}
                                className={`px-0.5 py-0.5 text-center border-r ${cellClass}`}
                              >
                                {isWorkHours ? renderProgram() : (
                                  <span className="text-[10px]">{cellContent}</span>
                                )}
                              </td>
                            );
                          })}
                          <td className="px-2 py-1.5 text-center font-semibold bg-green-50 border-l-2 border-green-300">
                            {totalOre}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Info text */}
          <div className="mt-4 text-xs text-slate-500 flex items-center gap-2">
            <span>💡</span>
            <span>
              Acest tabel este sincronizat automat cu Pontajul. Pentru a modifica orele, accesați tab-ul <strong>Pontaj</strong>.
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

export default EvidentaOreTab;
